<template>
    <div>
        <div class="kgr-row">
            <h3 class="kgr-headline kgr-headline--h3">
                {{ $t('Bankkonten') }}
            </h3>
        </div>
        <div class="kgr-row">
            <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                <div class="kgr-grid__cell">
                    <kgr-checkbox
                        v-model="filter.showInactive"
                        :label="$t('Inaktive Bankkonten anzeigen')"
                    />
                </div>
                <div class="kgr-grid__cell">
                    <button
                        class="kgr-button kgr-button--accent kgr-float kgr-float--right"
                        @click="openCreateDialog()"
                    >
                        {{ $t('Neues Mitglieder-Konto anlegen') }}
                    </button>
                </div>
            </div>
        </div>
        <table class="kgr-table kgr-table--block kgr-table--striped printable">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Inhaber') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('IBAN') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('BIC') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Überweisungskonto') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Startdatum des Überweisungskontos') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td
                        colspan="6"
                        style="text-align: center;"
                    >
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="bankAccounts.length === 0">
                    <td
                        colspan="6"
                        style="text-align: center;"
                    >
                        {{ $t('dataTable.noData') }}
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="bankAccount in filteredBankAccounts"
                        :key="bankAccount.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            {{ bankAccount.owner }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ bankAccount.iban }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ bankAccount.bic }}
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-icon
                                v-if="null !== bankAccount.creditTransferStartingAt"
                                :style="bankAccount.isCurrentCreditTransfer ? 'color: green;' : ''"
                                icon-class="check"
                                disabled
                            />
                        </td>
                        <td class="kgr-table__cell">
                            <template v-if="null !== bankAccount.creditTransferStartingAt">
                                {{ $t('gültig ab') }} {{ formatDate(bankAccount.creditTransferStartingAt) }}
                            </template>
                        </td>
                        <td class="kgr-table__cell">
                            <button
                                class="kgr-table__action"
                                :disabled="bankAccount.isArchived"
                                @click="openEditDialog(bankAccount)"
                            >
                                <kgr-icon
                                    icon-class="edit"
                                    :disabled="bankAccount.isArchived"
                                />
                            </button>
                            <button
                                class="kgr-table__action"
                                :disabled="!bankAccount.isDeletable"
                                @click="deleteBankAccount(bankAccount.id)"
                            >
                                <kgr-icon
                                    icon-class="delete"
                                    :disabled="!bankAccount.isDeletable"
                                />
                            </button>
                            <button
                                class="kgr-table__action"
                                :disabled="bankAccount.isDeletable || bankAccount.isArchived"
                                @click="archiveBankAccount(bankAccount.id)"
                            >
                                <kgr-icon
                                    icon-class="archive"
                                    :disabled="bankAccount.isDeletable || bankAccount.isArchived"
                                />
                            </button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <kgr-lightbox
            v-if="modalBankAccount"
            :value="showEditDialog"
            animated
            @post-input="closeEditModal"
        >
            <kgr-form-row
                :label="$t('Kontoinhaber')"
                :error="error"
                field-name="owner"
            >
                <kgr-text-input v-model="modalBankAccount.owner" />
            </kgr-form-row>
            <kgr-form-row
                v-if="modalBankAccount.isCreditTransferStartEditable"
                :label="$t('Standard-Konto für Überweisungen (Gutschriften, Storno)')"
                :error="error"
                field-name="withDate"
            >
                <kgr-checkbox v-model="modalBankAccount.withDate" />
            </kgr-form-row>
            <kgr-form-row
                v-if="modalBankAccount.isCreditTransferStartEditable && modalBankAccount.withDate"
                :label="$t('Gültig ab')"
                :error="error"
                field-name="creditTransferStartingAt"
            >
                <kgr-date-picker
                    v-model="modalBankAccount.creditTransferStartingAt"
                />
            </kgr-form-row>
            <hr class="kgr-separator--light" />
            <div class="kgr-row">
                <button
                    class="kgr-button"
                    @click="closeEditModal()"
                >
                    {{ $t('cancel') }}
                </button>
                <button
                    class="kgr-button kgr-button--accent"
                    :disabled="!hasModalChanges || loading"
                    @click="updateBankAccount(modalBankAccount)"
                >
                    {{ $t('save') }}
                </button>
            </div>
        </kgr-lightbox>
        <kgr-lightbox
            v-if="newBankAccount"
            :value="showDialog"
            :headline="$t('Bankkonto hinzufügen')"
            animated
            @post-input="closeModal"
        >
            <div>
                <kgr-form-row
                    :label="$t('Kontoinhaber')"
                    :error="error"
                    field-name="owner"
                >
                    <kgr-text-input v-model="newBankAccount.owner" />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('IBAN')"
                    :error="error"
                    field-name="iban"
                >
                    <kgr-text-input v-model="newBankAccount.iban" />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('BIC')"
                    :error="error"
                    field-name="bic"
                >
                    <kgr-text-input v-model="newBankAccount.bic" />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Standard-Konto für Überweisungen (Gutschriften, Storno)')"
                    :error="error"
                    field-name="withDate"
                >
                    <kgr-checkbox v-model="newBankAccount.withDate" />
                </kgr-form-row>
                <kgr-form-row
                    v-if="newBankAccount.withDate"
                    :label="$t('Gültig ab')"
                    :error="error"
                    field-name="creditTransferStartingAt"
                >
                    <kgr-date-picker
                        v-model="newBankAccount.creditTransferStartingAt"
                    />
                </kgr-form-row>
                <hr class="kgr-separator--light" />
                <div class="kgr-row">
                    <button
                        class="kgr-button"
                        @click="closeModal()"
                    >
                        {{ $t('cancel') }}
                    </button>
                    <button
                        class="kgr-button kgr-button--accent"
                        :disabled="loading"
                        @click="createBankAccount"
                    >
                        {{ $t('save') }}
                    </button>
                </div>
            </div>
        </kgr-lightbox>
    </div>
</template>

<script>
import { format, parse, parseISO } from '@legacy/js/date/date-fns.js';
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import DatePicker from '../../../components/common/form/picker/DatePicker.vue';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import Icon from '../../../components/common/icon/Icon.vue';
import Lightbox from '../../../components/common/modal/Lightbox.vue';
import Loading from '../../../components/common/Loading.vue';
import TextInput from '../../../components/common/form/input/TextInput.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-loader': Loading,
        'kgr-date-picker': DatePicker,
        'kgr-form-row': FormRow,
        'kgr-lightbox': Lightbox,
        'kgr-checkbox': Checkbox,
        'kgr-text-input': TextInput,
        'kgr-icon': Icon,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        id: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: false,
            showDialog: false,
            showEditDialog: false,
            initialLoading: true,
            bankAccounts: [],
            newBankAccount: null,
            modalBankAccount: null,
            filter: {
                showInactive: false,
            },
        };
    },
    computed: {
        occupiedDates() {
            return this.bankAccounts.filter((bankAccount) => bankAccount.creditTransferStartingAt !== null).map((bankAccount) => bankAccount.creditTransferStartingAt);
        },
        filterText() {
            return this.filter.showInactive ? this.$t('Alle Bankkonten werden angezeigt') : this.$t('Archivierte Bankkonten sind ausgeblendet');
        },
        filteredBankAccounts() {
            if (!this.filter.showInactive) {
                return this.bankAccounts.filter((bankAccount) => !bankAccount.isArchived);
            }
            return this.bankAccounts;
        },
        minDate() {
            return new Date();
        },
        isModalOwnerChanged() {
            return this.modalBankAccount.owner.trim() !== this.modalBankAccount.originalOwner;
        },
        isModalStartChanged() {
            return this.modalBankAccount.isCreditTransferStartEditable
                && this.modalBankAccount.withDate
                && this.modalBankAccount.creditTransferStartingAt !== null
                && this.modalBankAccount.creditTransferStartingAt !== this.modalBankAccount.originalCreditTransferStartingAt;
        },
        hasModalChanges() {
            return this.isModalOwnerChanged || this.isModalStartChanged;
        },
    },
    created() {
        this.loadBankAccounts();
    },
    methods: {
        updateBankAccount(bankAccount) {
            if (this.loading) {
                return;
            }
            this.loading = true;

            const requests = [];
            if (this.isModalOwnerChanged) {
                requests.push(() => axios.patch(`/club/member-bank-accounts/${bankAccount.id}/owner`, {
                    owner: bankAccount.owner.trim(),
                }));
            }
            if (this.isModalStartChanged) {
                const creditTransferStartingAt = parse(bankAccount.creditTransferStartingAt, 'dd.MM.yyyy', new Date());
                requests.push(() => axios.patch(`/club/member-bank-accounts/${bankAccount.id}/credit-transfer`, {
                    creditTransferStartingAt: format(creditTransferStartingAt, 'yyyy-MM-dd'),
                }));
            }

            requests
                .reduce((previous, request) => previous.then(request), Promise.resolve())
                .then(() => {
                    this.closeEditModal();
                    // loadBankAccounts() owns the flag from here: clearing it now would re-enable
                    // the save buttons while the reload is still in flight.
                    this.loadBankAccounts();
                })
                .catch((error) => {
                    this.error = Error.fromResponse(error.response);
                    // The name may already be saved when the start date is refused, so the table
                    // must not keep showing the old one; the reload also clears the flag.
                    this.loadBankAccounts();
                });
        },
        formatDate(dateOrNull) {
            if (dateOrNull === null) {
                return '-';
            }
            return format(parseISO(dateOrNull), 'dd.MM.yyyy');
        },
        loadBankAccounts() {
            this.loading = true;
            axios
                .get(`/club/members/${this.id}/member-bank-accounts`)
                .then((response) => {
                    this.bankAccounts = response.data.data;
                })
                // In a finally, so a failed reload cannot leave the flag stuck: the save buttons
                // are disabled while it is set.
                .finally(() => {
                    this.loading = false;
                    this.initialLoading = false;
                });
        },
        createBankAccount() {
            if (this.loading) {
                return;
            }
            this.loading = true;

            let formattedDate = null;
            if (this.newBankAccount.withDate && this.newBankAccount.creditTransferStartingAt !== null) {
                const creditTransferStartingAt = parse(this.newBankAccount.creditTransferStartingAt, 'dd.MM.yyyy', new Date());
                formattedDate = format(creditTransferStartingAt, 'yyyy-MM-dd');
            }

            const payload = {
                owner: this.newBankAccount.owner.trim(),
                iban: this.newBankAccount.iban.trim(),
                creditTransferStartingAt: formattedDate,
            };
            if (this.newBankAccount.bic) {
                payload.bic = this.newBankAccount.bic.trim();
            }

            axios
                .post(`/club/members/${this.id}/member-bank-accounts`, payload)
                .then(() => {
                    // loadBankAccounts() owns the flag from here: clearing it now would re-enable
                    // the save buttons while the reload is still in flight.
                    this.loadBankAccounts();
                    this.closeModal();
                    this.$emit('save');
                })
                .catch((error) => {
                    this.error = Error.fromResponse(error.response);
                    this.loading = false;
                });
        },
        archiveBankAccount(id) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du das Konto wirklich archivieren?'), () => {
                axios
                    .put(`/club/member-bank-accounts/${id}/archive`)
                    .then(() => {
                        this.loadBankAccounts();
                        this.$emit('save');
                    })
                    .catch(() => this.loading = false);
            }, this.$t('Archivieren'));
        },
        deleteBankAccount(id) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du das Konto wirklich löschen?'), () => {
                this.loading = true;
                axios
                    .delete(`/club/member-bank-accounts/${id}`)
                    .then(() => {
                        this.loadBankAccounts();
                        this.$emit('save');
                    })
                    .catch(() => this.loading = false);
            }, this.$t('Löschen'));
        },
        initNew() {
            this.error = new Error();
            this.newBankAccount = {
                owner: '',
                iban: '',
                bic: '',
                withDate: false,
                creditTransferStartingAt: format(new Date(), 'dd.MM.yyyy'),
            };
        },
        openCreateDialog() {
            this.initNew();
            this.showDialog = true;
        },
        closeModal() {
            this.showDialog = false;
        },
        openEditDialog(bankAccount) {
            this.setModalBankAccount(bankAccount);
            this.showEditDialog = true;
        },
        closeEditModal() {
            this.showEditDialog = false;
            this.modalBankAccount = null;
        },
        setModalBankAccount(bankAccount) {
            this.error = new Error();
            let date = format(new Date(), 'dd.MM.yyyy');
            if (bankAccount.creditTransferStartingAt !== null) {
                date = format(parseISO(bankAccount.creditTransferStartingAt), 'dd.MM.yyyy');
            }
            this.modalBankAccount = {
                id: bankAccount.id,
                owner: bankAccount.owner,
                originalOwner: bankAccount.owner,
                isCreditTransferStartEditable: bankAccount.isCreditTransferStartEditable,
                withDate: bankAccount.creditTransferStartingAt !== null,
                creditTransferStartingAt: date,
                originalCreditTransferStartingAt: bankAccount.creditTransferStartingAt !== null ? date : null,
            };
        },
    },
};
</script>
